/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae landing page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useNavigate, useParams } from 'react-router';

import { Box, Grid2, Paper, TextField } from '@mui/material';

import { ISaeSearchContext, SaeBreadcrumbs, SaeCollapsibleTable, SaeSearchContext, SaeStatus } from '@ngt/opms-sae';

import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';

import {
    ALL_INSTITUTIONS_CODE,
    InstitutionsContext,
    PageLayout,
    TrialContextSelector
} from '@ngt/opms';

import { debounce } from "lodash-es";
import { useContext } from 'react';
import useSaeColumns from '../../hooks/sae/useSaeColumns';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * used to get column spec for the sae table.
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

// interface ISaesPageParams {
//     institutionCode?: string;
// }

interface ISaesPageProps {
}


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const SaeSearch = () => {
    const { setPatientStudyNumber, setSaeNumber } = useContext(SaeSearchContext);

    const debouncedSetPatientStudyNumber = React.useRef(debounce(value => setPatientStudyNumber(value), 500));

    const debouncedSetSaeNumber = React.useRef(debounce(value => setSaeNumber(value), 500));

    const patientStudyNumberOnChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSetPatientStudyNumber.current(event.target.value);
    }, [debouncedSetPatientStudyNumber]);

    const saeNumberOnChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        event.target.value = event.target.value.replace(/[^0-9]/g, '');

        debouncedSetSaeNumber.current(event.target.value);
    }, [debouncedSetSaeNumber]);

    return (
        <>
            <Grid2
                size={{
                    xs: 12,
                    sm: 4
                }}
                sx={{ display: 'flex', alignItems: 'end' }}
            >
                <TextField
                    label="Patient"
                    variant="standard"
                    onChange={patientStudyNumberOnChange}
                    fullWidth
                />
            </Grid2>
            <Grid2
                size={{
                    xs: 12,
                    sm: 4
                }}
                sx={{ display: 'flex', alignItems: 'end' }}
            >
                <TextField
                    label="SAE Number"
                    variant="standard"
                    onChange={saeNumberOnChange}
                    fullWidth
                />
            </Grid2>
        </>
    );
}

const pageSizeOptions = [5, 10, 20, 50, 100];

const SaesPage: React.FunctionComponent<ISaesPageProps> = () => {
    const navigate = useNavigate();

    const { institutionCode } = useParams();

    const contextInstitutions = React.useContext(InstitutionsContext);

    const institutions = contextInstitutions?.data;

    const institution = React.useMemo(() => {
        return institutions?.find(i => i.code === institutionCode);
    }, [institutions, institutionCode]);

    const {
        permissions: [
            canViewSae,
            canAdministerSae
        ], 
        loading: permissionsLoading 
    } = usePermissionsByIds(
        [
            Permission.SaeView,
            Permission.SaeAdminister
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const onSelect = React.useCallback((_newMasterGroupCode?: string | null, _newCollaboratingGroupCode?: string | null, _newCountryCode?: string | null, newInstitutionCode?: string | null) => {
        if (newInstitutionCode) {
            navigate(`/sae/${newInstitutionCode}`);
        }
        else {
            navigate(`/sae`);
        }

        return;
    }, [navigate]);

    const showInstitutions = React.useMemo(() => {
        return !institutionCode
    }, [institutionCode]);

    const saeDataColumns = useSaeColumns(contextInstitutions.data, showInstitutions) as any;

    const invalidSaeDataColumns = useSaeColumns(contextInstitutions.data, showInstitutions, true) as any;

    React.useEffect(() => {
        if (!permissionsLoading) {
            if (!canViewSae) {
                navigate(`/error/403`, { replace: true });
            }
        }
    }, [institutions, canViewSae, canAdministerSae, permissionsLoading, navigate]);

    const [patientStudyNumber, setPatientStudyNumber] = React.useState<string | undefined>(undefined);

    const [saeNumber, setSaeNumber] = React.useState<string | undefined>(undefined);

    const saeSearchContext: ISaeSearchContext = React.useMemo(() => {
        return {
            patientStudyNumber,
            setPatientStudyNumber: (patientStudyNumber) => setPatientStudyNumber(patientStudyNumber),
            saeNumber,
            setSaeNumber: (saeNumber) => setSaeNumber(saeNumber)
        }

    }, [patientStudyNumber, setPatientStudyNumber, saeNumber, setSaeNumber]);

    return (
        <PageLayout
            breadcrumbs={<SaeBreadcrumbs institution={institution} />}
            heading='Serious Adverse Events'
        >
            <Paper
                sx={{p: 3}}
            >
                <SaeSearchContext.Provider value={saeSearchContext}>
                    <Box sx={{mb: 2}}>
                        <TrialContextSelector
                            onChange={onSelect}
                            allowAllMasterGroups={false}
                            allowAllCollaboratingGroups={false}
                            allowAllCountries={false}
                            allowAllInstitutions={true}
                            hideMasterGroups={true}
                            hideCollaboratingGroups={true}
                            hideCountries={true}
                            masterGroupCode={null}
                            collaboratingGroupCode={null}
                            countryCode={null}
                            institutionCode={institution?.code ?? ALL_INSTITUTIONS_CODE}
                            sm={4}
                        >
                            <SaeSearch />
                        </TrialContextSelector>
                    </Box>

                    {
                        !!canAdministerSae &&
                        <SaeCollapsibleTable
                            columns={saeDataColumns}
                            statuses={[SaeStatus.New, SaeStatus.Added]}
                            title={"New SAEs"}
                            pluralizeTitle={false}
                            disabledTableText={"There are no new SAEs"}
                            showInvestigatorColumn
                            showMedicalReviewColumn
                            pageSizeOptions={pageSizeOptions}
                            initialState={{ pagination: { paginationModel: { pageSize: 10 } }}}
                        />
                    }

                    <SaeCollapsibleTable
                        key={`${(institution?.code ?? ALL_INSTITUTIONS_CODE)}-awaiting`}
                        columns={saeDataColumns}
                        statuses={[SaeStatus.AwaitingForReview]}
                        title={"Awaiting review SAEs"}
                        pluralizeTitle={false}
                        disabledTableText={"There are no awaiting for review SAEs"}
                        showInvestigatorColumn
                        showMedicalReviewColumn
                        pageSizeOptions={pageSizeOptions}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } }}}
                    />

                    <SaeCollapsibleTable
                        key={`${(institution?.code ?? ALL_INSTITUTIONS_CODE)}-approved`}
                        columns={saeDataColumns}
                        statuses={[SaeStatus.Approved]}
                        title={"Approved SAEs"}
                        pluralizeTitle={false}
                        disabledTableText={"There are no approved SAEs"}
                        showInvestigatorColumn={false}
                        showMedicalReviewColumn
                        pageSizeOptions={pageSizeOptions}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } }}}
                    />

                    <SaeCollapsibleTable
                        key={`${(institution?.code ?? ALL_INSTITUTIONS_CODE)}-rejected`}
                        columns={saeDataColumns}
                        statuses={[SaeStatus.Rejected]}
                        title={"Rejected SAEs"}
                        pluralizeTitle={false}
                        disabledTableText={"There are no rejected SAEs"}
                        showInvestigatorColumn={false}
                        showMedicalReviewColumn
                        pageSizeOptions={pageSizeOptions}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } }}}
                    />

                    <SaeCollapsibleTable
                        key={`${(institution?.code ?? ALL_INSTITUTIONS_CODE)}-invalid`}
                        columns={invalidSaeDataColumns}
                        statuses={[SaeStatus.Invalid]}
                        title={"Invalid SAEs"}
                        pluralizeTitle={false}
                        disabledTableText={"There are no invalid SAEs"}
                        showInvestigatorColumn={false}
                        showMedicalReviewColumn={false}
                        pageSizeOptions={pageSizeOptions}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } }}}
                    />
                </SaeSearchContext.Provider>
            </Paper>
        </PageLayout>
    );
}


/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default SaesPage;